import React from "react";
import "./QuickLinks.css";
import thanksIcon from '../assets/icons/thanks.svg'
import msgIcon from '../assets/icons/msg.svg'
import callIcon from '../assets/icons/call.svg'
import docIcon from '../assets/icons/doc.svg'

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Heading from "./Heading";


/**
 * QuickLinks component (data-driven)
 * - Accepts `items` prop which is an array of objects: { key, title, description, icon, href, coords }
 * - If any item has `coords` and `mapBackground` prop is provided, the component will render a "map" layout
 *   where items are absolutely positioned over the background using the coords { left: '10%', top: '30%' }.
 * - Otherwise it renders a responsive grid of cards.
 *
 * Usage examples:
 * <QuickLinks items={data} />
 * <QuickLinks items={mapData} mapBackground="/images/map.png" />
 */

const defaultDataEn = [
  {
    key: "schemes",
    title: "Government Schemes",
    description: "Overview of all central and state schemes",
    icon: <img loading="lazy" src={thanksIcon} alt="Schemes" style={{ width: 24, height: 24 }} />,
    bg: "linear-gradient(135deg, #FF6900 0%, #F54900 100%)",
    href: "/schemes-projects#schemes",
  },
  {
    key: "services",
    title: "Citizen Services",
    description: "Various citizen services and applications",
    icon: <img loading="lazy" src={docIcon} alt="Services" style={{ width: 24, height: 24 }} />,
    bg: "linear-gradient(135deg, #2B7FFF 0%, #155DFC 100%)",
    href: "/citizen-services",
  },
  {
    key: "complaints",
    title: "Complaints",
    description: "Register complaints and follow-up",
    icon: <img loading="lazy" src={msgIcon} alt="Complaints" style={{ width: 24, height: 24 }} />,
    bg: "linear-gradient(135deg, #00C950 0%, #00A63E 100%)",
    href: "/citizen-services?tab=2",
  },
  {
    key: "contact",
    title: "Contact",
    description: "Connect with us",
    icon: <img loading="lazy" src={callIcon} alt="Contact" style={{ width: 24, height: 24 }} />,
    bg: "linear-gradient(135deg, #00B8DB 0%, #0092B8 100%)",
    href: "/gram-panchayat#address",
  },
];

const defaultDataMr = [
  {
    key: "schemes",
    title: "सरकारी योजना",
    description: "सर्व केंद्रीय आणि राज्य योजनांचे अवलोकन करा",
    icon: <img loading="lazy" src={thanksIcon} alt="Schemes" style={{ width: 24, height: 24 }} />,
    bg: "linear-gradient(135deg, #FF6900 0%, #F54900 100%)",
    href: "/schemes-projects#schemes",
  },
  {
    key: "services",
    title: "नागरिक सेवा",
    description: "विविध नागरिक सेवा आणि अर्ज",
    icon: <img loading="lazy" src={docIcon} alt="Services" style={{ width: 24, height: 24 }} />,
    bg: "linear-gradient(135deg, #2B7FFF 0%, #155DFC 100%)",
    href: "/citizen-services",
  },
  {
    key: "complaints",
    title: "तक्रारी",
    description: "तक्रारी नोंदविणे व फॉलो-अप",
    icon: <img loading="lazy" src={msgIcon} alt="Complaints" style={{ width: 24, height: 24 }} />,
    bg: "linear-gradient(135deg, #00C950 0%, #00A63E 100%)",
    href: "/citizen-services?tab=2",
  },
  {
    key: "contact",
    title: "संपर्क",
    description: "आमच्याशी संपर्क साधा",
    icon: <img loading="lazy" src={callIcon} alt="Contact" style={{ width: 24, height: 24 }} />,
    bg: "linear-gradient(135deg, #00B8DB 0%, #0092B8 100%)",
    href: "/gram-panchayat#address",
  },
];

const QuickLinks = ({ items, mapBackground }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Get default data based on current language if no items provided
  const defaultData = i18n.language === "mr" ? defaultDataMr : defaultDataEn;
  const linksData = items || defaultData;

  // Determine if we should render map mode: any item has coords and we have a background
  const mapMode = !!mapBackground && linksData.some((it) => it.coords);

  return (
    <section className="quick-links-section section-padding">
      <Heading title={t('quickLinks.title')} />

      {mapMode ? (
        <div
          className="quick-links-map-container"
          style={{
            backgroundImage: `url(${mapBackground})`,
          }}
        >
          {/* Render each item positioned by coords (left/top as percent or px) */}
          {linksData.map((it) => {
            if (!it.coords) return null; // skip items without coords in map mode
            const { left = "50%", top = "50%" } = it.coords;
            return (
              <a
                key={it.key}
                href={it.href || "#"}
                className="quick-links-map-item"
                style={{
                  left,
                  top,
                }}
              >
                <div
                  className="quick-links-map-icon"
                  style={{
                    background:
                      it.bg || "linear-gradient(135deg,#6EE7B7,#3B82F6)",
                  }}
                >
                  {it.icon}
                </div>
                <div className="quick-links-map-content">
                  <div className="quick-links-map-title">{it.title}</div>
                  <div className="quick-links-map-description">
                    {it.description}
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      ) : (
        <div className="quick-links-grid">
          {linksData.map((it) => (
            <div key={it.key} className="quick-links-card-wrapper">
              <div
                className="quick-links-card"
                onClick={() => {
                  if (it.href) navigate(it.href);
                }}
              >
                <div className="quick-links-card-content">
                  <div
                    className="quick-links-card-icon"
                    style={{
                      background:
                        it.bg || "linear-gradient(135deg,#6EE7B7,#3B82F6)",
                    }}
                  >
                    {it.icon}
                  </div>
                  <h4 className="quick-links-card-title">{it.title}</h4>
                  <p className="quick-links-card-description">
                    {it.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default QuickLinks;
